import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { Plus, Trash2, FileText } from "lucide-react";
import {
  createCarePlanSchema,
  createInterventionSchema,
  createMrpSchema,
  scheduleFollowUpSchema,
  type CreateCarePlanInput,
  type CreateInterventionInput,
  type CreateMrpInput,
  type ScheduleFollowUpInput,
} from "@dosa/validation";
import type { AdherenceLevel, CarePlan, Intervention, MedicationRelatedProblem, PatientMedication } from "@dosa/types";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChipSelect,
  EmptyState,
  FieldError,
  FieldLabel,
  Input,
  MrpCategoryBadge,
  MrpSeverityBadge,
  PageLoading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StepLadder,
  Tabs,
  TabsContent,
  Textarea,
  buttonVariants,
  cn,
} from "@dosa/ui";
import { useAuth } from "../../hooks/useAuth";
import {
  addIntervention,
  addMrp,
  completeMtmAssessment,
  createCarePlan,
  getCarePlan,
  getMtmAssessment,
  getPatient,
  listInterventions,
  listMrps,
  listPatientMedications,
  resolveMrp,
  scheduleFollowUp,
  updateMtmAssessment,
} from "../../lib/api-client";

const INTERVENTION_TYPES = [
  "PATIENT_EDUCATION",
  "MEDICATION_ADJUSTMENT_RECOMMENDATION",
  "PRESCRIBER_COMMUNICATION",
  "ADHERENCE_SUPPORT",
  "REFERRAL",
  "MONITORING_RECOMMENDATION",
  "OTHER",
] as const;

const MTM_STEP_KEYS = ["reconciliation", "assessment", "mrps", "interventions", "carePlan", "followUp"] as const;
type MtmStepKey = (typeof MTM_STEP_KEYS)[number];

export function PharmacistAssessmentPage() {
  const { t } = useTranslation();
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState<MtmStepKey>("reconciliation");

  const assessmentQuery = useQuery({
    queryKey: ["assessment", assessmentId],
    queryFn: () => getMtmAssessment(assessmentId!),
    enabled: !!assessmentId,
  });
  const patientId = assessmentQuery.data?.patientId;

  const patientQuery = useQuery({ queryKey: ["patient", patientId], queryFn: () => getPatient(patientId!), enabled: !!patientId });
  const medsQuery = useQuery({ queryKey: ["patient-medications", patientId], queryFn: () => listPatientMedications(patientId!), enabled: !!patientId });
  const mrpsQuery = useQuery({ queryKey: ["mrps", assessmentId], queryFn: () => listMrps(assessmentId!), enabled: !!assessmentId });
  const interventionsQuery = useQuery({ queryKey: ["interventions", assessmentId], queryFn: () => listInterventions(assessmentId!), enabled: !!assessmentId });
  const carePlanQuery = useQuery({ queryKey: ["care-plan", assessmentId], queryFn: () => getCarePlan(assessmentId!), enabled: !!assessmentId });

  const completeMutation = useMutation({
    mutationFn: () => completeMtmAssessment(assessmentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] });
      navigate(`/pharmacist/reports/${assessmentId}`);
    },
  });

  if (assessmentQuery.isLoading || patientQuery.isLoading) return <PageLoading />;
  if (!assessmentQuery.data || !patientQuery.data) return <EmptyState title={t("common.noResults")} />;

  const assessment = assessmentQuery.data;
  const patient = patientQuery.data;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-[2.25rem] font-semibold leading-none text-charcoal-900">{t("pharmacist.mtm.title")}</h1>
          <p className="mt-2 text-charcoal-500">
            {patient.firstName} {patient.lastName} &middot; {assessment.type.replaceAll("_", " ")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={assessment.status === "COMPLETED" ? "success" : "info"}>{assessment.status}</Badge>
          {assessment.status === "COMPLETED" ? (
            <Link to={`/pharmacist/reports/${assessment.id}`} className={cn(buttonVariants({ variant: "outline" }))}>
              <FileText className="h-4 w-4" /> {t("pharmacist.carePlan.generateReport")}
            </Link>
          ) : (
            <Button onClick={() => completeMutation.mutate()} isLoading={completeMutation.isPending}>
              {t("pharmacist.mtm.completeAssessment")}
            </Button>
          )}
        </div>
      </div>

      <StepLadder
        steps={MTM_STEP_KEYS.map((k) => ({ label: t(`pharmacist.mtm.steps.${k}`) }))}
        activeIndex={MTM_STEP_KEYS.indexOf(activeStep) + 1}
        onStepClick={(n) => setActiveStep(MTM_STEP_KEYS[n - 1]!)}
      />

      <Tabs value={activeStep} onValueChange={(v) => setActiveStep(v as MtmStepKey)}>
        <TabsContent value="reconciliation">
          <Card>
            <CardContent className="p-0">
              {medsQuery.data?.length ? (
                <ul className="divide-y divide-charcoal-100">
                  {medsQuery.data.map((m) => (
                    <li key={m.id} className="flex items-center justify-between px-5 py-3 text-sm">
                      <div>
                        <p className="font-medium text-charcoal-800">{m.freeTextName}</p>
                        <p className="text-charcoal-400">{[m.dose, m.frequency?.replaceAll("_", " ")].filter(Boolean).join(" · ")}</p>
                      </div>
                      <Badge variant="neutral">{m.source.replaceAll("_", " ")}</Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState className="border-0" title={t("patient.medications.empty")} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="assessment">
          <AssessmentSummaryForm assessmentId={assessment.id} initialSummary={assessment.clinicalSummary} initialAdherence={assessment.adherenceAssessment?.level} />
        </TabsContent>

        <TabsContent value="mrps">
          <MrpTab assessmentId={assessment.id} patientId={patient.id} pharmacistId={user!.pharmacistId!} mrps={mrpsQuery.data ?? []} medications={medsQuery.data ?? []} />
        </TabsContent>

        <TabsContent value="interventions">
          <InterventionTab assessmentId={assessment.id} patientId={patient.id} pharmacistId={user!.pharmacistId!} interventions={interventionsQuery.data ?? []} mrps={mrpsQuery.data ?? []} />
        </TabsContent>

        <TabsContent value="carePlan">
          <CarePlanTab assessmentId={assessment.id} patientId={patient.id} pharmacistId={user!.pharmacistId!} carePlan={carePlanQuery.data} />
        </TabsContent>

        <TabsContent value="followUp">
          <FollowUpTab assessmentId={assessment.id} patientId={patient.id} pharmacistId={user!.pharmacistId!} carePlanId={carePlanQuery.data?.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function AssessmentSummaryForm({
  assessmentId,
  initialSummary,
  initialAdherence,
}: {
  assessmentId: string;
  initialSummary?: string;
  initialAdherence?: AdherenceLevel;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState(initialSummary ?? "");
  const [adherence, setAdherence] = useState<AdherenceLevel>(initialAdherence ?? "UNKNOWN");

  const mutation = useMutation({
    mutationFn: () =>
      updateMtmAssessment(assessmentId, {
        clinicalSummary: summary,
        adherenceAssessment: { level: adherence },
        status: "PENDING_REVIEW",
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assessment", assessmentId] }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pharmacist.mtm.clinicalSummary")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <FieldLabel htmlFor="clinicalSummary">{t("pharmacist.mtm.clinicalSummary")}</FieldLabel>
          <Textarea id="clinicalSummary" rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </div>
        <div className="max-w-xs">
          <FieldLabel htmlFor="adherence">{t("pharmacist.mtm.adherenceLevel")}</FieldLabel>
          <Select value={adherence} onValueChange={(v) => setAdherence(v as AdherenceLevel)}>
            <SelectTrigger id="adherence">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["HIGH", "MODERATE", "LOW", "UNKNOWN"].map((level) => (
                <SelectItem key={level} value={level}>
                  {level}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => mutation.mutate()} isLoading={mutation.isPending}>
          {t("common.save")}
        </Button>
      </CardContent>
    </Card>
  );
}

function MrpTab({
  assessmentId,
  patientId,
  pharmacistId,
  mrps,
  medications,
}: {
  assessmentId: string;
  patientId: string;
  pharmacistId: string;
  mrps: MedicationRelatedProblem[];
  medications: PatientMedication[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateMrpInput>({
    resolver: zodResolver(createMrpSchema),
    defaultValues: { mtmAssessmentId: assessmentId, category: "SAFETY", subType: "OTHER", severity: "MODERATE" },
  });

  const mutation = useMutation({
    mutationFn: (input: CreateMrpInput) => addMrp(patientId, pharmacistId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mrps", assessmentId] });
      reset({ mtmAssessmentId: assessmentId, category: "SAFETY", subType: "OTHER", severity: "MODERATE", description: "" });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => resolveMrp(id, "RESOLVED", "Resolved during assessment"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["mrps", assessmentId] }),
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("pharmacist.mrp.addMrp")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="grid gap-4 sm:grid-cols-2" noValidate>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor="category">{t("pharmacist.mrp.category")}</FieldLabel>
              <Controller
                control={control}
                name="category"
                render={({ field }) => (
                  <ChipSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={["INDICATION", "EFFECTIVENESS", "SAFETY", "ADHERENCE"].map((c) => ({
                      value: c,
                      label: t(`pharmacist.mrp.categories.${c}`),
                    }))}
                  />
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor="severity">{t("pharmacist.mrp.severity")}</FieldLabel>
              <Controller
                control={control}
                name="severity"
                render={({ field }) => (
                  <ChipSelect
                    value={field.value}
                    onChange={field.onChange}
                    equalWidth
                    className="max-w-md"
                    options={["LOW", "MODERATE", "HIGH", "CRITICAL"].map((s) => ({
                      value: s,
                      label: t(`pharmacist.mrp.severities.${s}`),
                    }))}
                  />
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor="relatedPatientMedicationId">{t("patient.nav.medications")}</FieldLabel>
              <Controller
                control={control}
                name="relatedPatientMedicationId"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    items={Object.fromEntries(medications.map((m) => [m.id, m.freeTextName ?? m.id]))}
                  >
                    <SelectTrigger id="relatedPatientMedicationId">
                      <SelectValue placeholder={t("patient.nav.medications")} />
                    </SelectTrigger>
                    <SelectContent>
                      {medications.map((m) => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.freeTextName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <FieldLabel htmlFor="description">{t("pharmacist.mrp.description")}</FieldLabel>
              <Textarea id="description" rows={3} {...register("description")} />
              <FieldError>{errors.description?.message}</FieldError>
            </div>
            <Button type="submit" isLoading={isSubmitting} className="sm:col-span-2 sm:w-fit">
              {t("common.add")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          {mrps.length === 0 ? (
            <EmptyState className="border-0" title={t("common.noResults")} />
          ) : (
            mrps.map((mrp) => (
              <div key={mrp.id} className="rounded-md border border-charcoal-100 p-4">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <MrpCategoryBadge category={mrp.category} label={t(`pharmacist.mrp.categories.${mrp.category}`)} />
                  <MrpSeverityBadge severity={mrp.severity} label={t(`pharmacist.mrp.severities.${mrp.severity}`)} />
                  <Badge variant={mrp.status === "RESOLVED" ? "success" : "neutral"}>{mrp.status}</Badge>
                </div>
                <p className="text-sm text-charcoal-700">{mrp.description}</p>
                {mrp.status !== "RESOLVED" && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={() => resolveMutation.mutate(mrp.id)}>
                    {t("pharmacist.mrp.resolve")}
                  </Button>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InterventionTab({
  assessmentId,
  patientId,
  pharmacistId,
  interventions,
  mrps,
}: {
  assessmentId: string;
  patientId: string;
  pharmacistId: string;
  interventions: Intervention[];
  mrps: MedicationRelatedProblem[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateInterventionInput>({
    resolver: zodResolver(createInterventionSchema),
    defaultValues: { mtmAssessmentId: assessmentId, type: "MEDICATION_ADJUSTMENT_RECOMMENDATION", communicatedToPrescriber: false },
  });

  const mutation = useMutation({
    mutationFn: (input: CreateInterventionInput) => addIntervention(patientId, pharmacistId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["interventions", assessmentId] });
      reset({ mtmAssessmentId: assessmentId, type: "MEDICATION_ADJUSTMENT_RECOMMENDATION", communicatedToPrescriber: false, recommendation: "" });
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("pharmacist.intervention.addIntervention")}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4" noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <FieldLabel htmlFor="type">{t("pharmacist.intervention.type")}</FieldLabel>
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={Object.fromEntries(INTERVENTION_TYPES.map((tOpt) => [tOpt, tOpt.replaceAll("_", " ")]))}
                    >
                      <SelectTrigger id="type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERVENTION_TYPES.map((tOpt) => (
                          <SelectItem key={tOpt} value={tOpt}>
                            {tOpt.replaceAll("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <FieldLabel htmlFor="mrpId">{t("pharmacist.mrp.title")}</FieldLabel>
                <Controller
                  control={control}
                  name="mrpId"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      items={Object.fromEntries(mrps.map((m) => [m.id, m.description.slice(0, 40)]))}
                    >
                      <SelectTrigger id="mrpId">
                        <SelectValue placeholder={t("common.optional")} />
                      </SelectTrigger>
                      <SelectContent>
                        {mrps.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.description.slice(0, 40)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>
            <div>
              <FieldLabel htmlFor="recommendation">{t("pharmacist.intervention.recommendation")}</FieldLabel>
              <Textarea id="recommendation" rows={3} {...register("recommendation")} />
              <FieldError>{errors.recommendation?.message}</FieldError>
            </div>
            <div>
              <FieldLabel htmlFor="rationale">{t("pharmacist.intervention.rationale")}</FieldLabel>
              <Textarea id="rationale" rows={2} {...register("rationale")} />
            </div>
            <label className="flex items-center gap-2 text-sm text-charcoal-700">
              <input type="checkbox" className="h-4 w-4" {...register("communicatedToPrescriber")} />
              {t("pharmacist.intervention.communicatedToPrescriber")}
            </label>
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.add")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 p-5">
          {interventions.length === 0 ? (
            <EmptyState className="border-0" title={t("common.noResults")} />
          ) : (
            interventions.map((iv) => (
              <div key={iv.id} className="rounded-md border border-charcoal-100 p-4 text-sm">
                <p className="font-medium text-charcoal-800">{iv.type.replaceAll("_", " ")}</p>
                <p className="mt-1 text-charcoal-600">{iv.recommendation}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CarePlanTab({
  assessmentId,
  patientId,
  pharmacistId,
  carePlan,
}: {
  assessmentId: string;
  patientId: string;
  pharmacistId: string;
  carePlan?: CarePlan | null;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateCarePlanInput>({
    resolver: zodResolver(createCarePlanSchema),
    defaultValues: {
      mtmAssessmentId: assessmentId,
      summary: carePlan?.summary ?? "",
      monitoringParameters: carePlan?.monitoringParameters ?? [{ parameter: "", frequency: "MONTHLY" }],
      medicationActionPlan: carePlan?.medicationActionPlan ?? [{ instruction: "" }],
    },
  });

  const monitoringArray = useFieldArray({ control, name: "monitoringParameters" });
  const actionArray = useFieldArray({ control, name: "medicationActionPlan" });

  const mutation = useMutation({
    mutationFn: (input: CreateCarePlanInput) => createCarePlan(patientId, pharmacistId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["care-plan", assessmentId] }),
  });

  if (carePlan) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("pharmacist.carePlan.title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-charcoal-700">{carePlan.summary}</p>
          <div>
            <p className="mb-2 text-sm font-medium text-charcoal-700">{t("pharmacist.carePlan.monitoringParameters")}</p>
            <ul className="space-y-1 text-sm text-charcoal-600">
              {carePlan.monitoringParameters.map((mp) => (
                <li key={mp.id}>
                  {mp.parameter} &middot; {mp.frequency}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-charcoal-700">{t("pharmacist.carePlan.actionPlan")}</p>
            <ul className="list-inside list-disc space-y-1 text-sm text-charcoal-600">
              {carePlan.medicationActionPlan.map((a) => (
                <li key={a.id}>{a.instruction}</li>
              ))}
            </ul>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pharmacist.carePlan.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-5" noValidate>
          <div>
            <FieldLabel htmlFor="summary">{t("pharmacist.carePlan.summary")}</FieldLabel>
            <Textarea id="summary" rows={4} {...register("summary")} />
            <FieldError>{errors.summary?.message}</FieldError>
          </div>

          <div>
            <FieldLabel>{t("pharmacist.carePlan.monitoringParameters")}</FieldLabel>
            <div className="space-y-2">
              {monitoringArray.fields.map((field, idx) => (
                <div key={field.id} className="flex gap-2">
                  <Input placeholder={t("pharmacist.carePlan.monitoringParameters")} {...register(`monitoringParameters.${idx}.parameter`)} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => monitoringArray.remove(idx)} aria-label={t("common.delete")}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => monitoringArray.append({ parameter: "", frequency: "MONTHLY" })}>
              <Plus className="h-4 w-4" /> {t("pharmacist.carePlan.addParameter")}
            </Button>
          </div>

          <div>
            <FieldLabel>{t("pharmacist.carePlan.actionPlan")}</FieldLabel>
            <div className="space-y-2">
              {actionArray.fields.map((field, idx) => (
                <div key={field.id} className="flex gap-2">
                  <Input placeholder={t("pharmacist.carePlan.actionPlan")} {...register(`medicationActionPlan.${idx}.instruction`)} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => actionArray.remove(idx)} aria-label={t("common.delete")}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => actionArray.append({ instruction: "" })}>
              <Plus className="h-4 w-4" /> {t("pharmacist.carePlan.addActionItem")}
            </Button>
          </div>

          <Button type="submit" isLoading={isSubmitting}>
            {t("common.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function FollowUpTab({
  assessmentId,
  patientId,
  pharmacistId,
  carePlanId,
}: {
  assessmentId: string;
  patientId: string;
  pharmacistId: string;
  carePlanId?: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ScheduleFollowUpInput>({
    resolver: zodResolver(scheduleFollowUpSchema),
    defaultValues: { mtmAssessmentId: assessmentId, carePlanId },
  });

  const mutation = useMutation({
    mutationFn: (input: ScheduleFollowUpInput) => scheduleFollowUp(patientId, pharmacistId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patient-follow-ups", patientId] });
      reset({ mtmAssessmentId: assessmentId, carePlanId, dueDate: "" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("pharmacist.followUp.schedule")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="grid max-w-sm gap-4" noValidate>
          <div>
            <FieldLabel htmlFor="dueDate">{t("pharmacist.followUp.dueDate")}</FieldLabel>
            <Input id="dueDate" type="date" {...register("dueDate")} />
            <FieldError>{errors.dueDate?.message}</FieldError>
          </div>
          <div>
            <FieldLabel htmlFor="reason">{t("pharmacist.followUp.reason")}</FieldLabel>
            <Textarea id="reason" rows={3} {...register("reason")} />
          </div>
          <Button type="submit" isLoading={isSubmitting}>
            {t("pharmacist.followUp.schedule")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
